import { IconHistory } from '@tabler/icons-react';
import { useTranslation } from '../../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../../utils/bnFormat';

const CATEGORY_STYLE = {
    Science: { card: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/30 dark:border-blue-900/40 dark:text-blue-300', icon: '🔬' },
    Technology: { card: 'bg-green-50 border-green-200 text-green-700 dark:bg-green-950/30 dark:border-green-900/40 dark:text-green-300', icon: '💡' },
    History: { card: 'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/30 dark:border-orange-900/40 dark:text-orange-300', icon: '📜' },
    Literature: { card: 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-950/30 dark:border-purple-900/40 dark:text-purple-300', icon: '📖' },
    Music: { card: 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/30 dark:border-rose-900/40 dark:text-rose-300', icon: '🎵' },
    Art: { card: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/30 dark:border-amber-900/40 dark:text-amber-300', icon: '🎨' },
    Politics: { card: 'bg-sky-50 border-sky-200 text-sky-700 dark:bg-sky-950/30 dark:border-sky-900/40 dark:text-sky-300', icon: '🏛️' },
    Sports: { card: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-300', icon: '🏆' },
    Space: { card: 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/30 dark:border-indigo-900/40 dark:text-indigo-300', icon: '🚀' },
    Medicine: { card: 'bg-pink-50 border-pink-200 text-pink-700 dark:bg-pink-950/30 dark:border-pink-900/40 dark:text-pink-300', icon: '⚕️' },
    'Civil Rights': { card: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950/30 dark:border-yellow-900/40 dark:text-yellow-300', icon: '✊' },
    Culture: { card: 'bg-violet-50 border-violet-200 text-violet-700 dark:bg-violet-950/30 dark:border-violet-900/40 dark:text-violet-300', icon: '🌟' },
    Exploration: { card: 'bg-teal-50 border-teal-200 text-teal-700 dark:bg-teal-950/30 dark:border-teal-900/40 dark:text-teal-300', icon: '🧭' },
    Nature: { card: 'bg-lime-50 border-lime-200 text-lime-800 dark:bg-lime-950/30 dark:border-lime-900/40 dark:text-lime-300', icon: '🌿' },
};

function formatYear(year, lang) {
    if (lang !== 'bn') return year;
    return toBnDigits(year);
}

export default function BirthdayEventCard({ event, lang }) {
    const { t } = useTranslation();
    const cat = CATEGORY_STYLE[event.category] ?? CATEGORY_STYLE.History;
    const categoryLabel = t(`ageCalculator.eventCategories.${event.category}`, event.category);
    const title = lang === 'bn' ? event.title_bn : event.title_en;
    const description = lang === 'bn' ? event.event_bn : event.event_en;

    return (
        <div
            data-export-birthday-event-box
            className="flex h-full flex-col overflow-hidden rounded-xl border border-[#E8E8EE] bg-white dark:border-zinc-700 dark:bg-zinc-900"
        >
            <div className="flex items-center gap-2 border-b border-orange-200 bg-orange-50 px-3.5 py-2.5 dark:border-orange-900/40 dark:bg-orange-950/20">
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-orange-100 text-orange-600 dark:bg-orange-950/40 dark:text-orange-400">
                    <IconHistory size={13} stroke={1.9} />
                </span>
                <p className="text-[12px] font-bold leading-tight text-orange-800 dark:text-orange-300">
                    {t('ageCalculator.birthdayEventTitle')}
                </p>
            </div>

            <div
                data-export-birthday-event-highlight
                className="border-b border-orange-200 bg-gradient-to-b from-orange-50 to-white px-4 py-4 text-center dark:border-orange-900/40 dark:from-orange-950/20 dark:to-zinc-900"
            >
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6B6B75] dark:text-zinc-500">
                    {t('ageCalculator.birthdayEventYear')}
                </p>
                <p
                    data-export-birthday-event-year
                    className="mb-2.5 text-[32px] font-black leading-none text-primary tabular-nums"
                >
                    {formatYear(event.year, lang)}
                </p>
                <p
                    data-export-birthday-event-title
                    className="mx-auto max-w-[280px] text-[14px] font-extrabold leading-snug text-orange-800 dark:text-orange-300"
                >
                    {title}
                </p>
            </div>

            <div className="flex flex-1 flex-col gap-2.5 px-3.5 py-3">
                <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold ${cat.card}`}>
                    <span className="text-[14px] leading-none">{cat.icon}</span>
                    {categoryLabel}
                </span>
                <p
                    data-export-birthday-event-desc
                    className="text-[12px] leading-relaxed text-[#6B6B75] dark:text-zinc-400"
                >
                    {description}
                </p>
            </div>

            <p
                data-export-birthday-event-footer
                className="border-t border-[#F0F0F6] bg-[#FAFAFC] px-3.5 py-2.5 text-center text-[11px] italic text-[#6B6B75] dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-500"
            >
                {t('ageCalculator.birthdayEventFooter')}
            </p>
        </div>
    );
}
